import type { CreateReminderBody } from '@kanto/shared';
import { useState, type FormEvent, type ReactElement } from 'react';
import { fromLocalInput } from '../lib/dates';
import { useT } from '../lib/i18n';
import { TagInput } from './TagInput';

/**
 * The field at the top of the list, and the only way a reminder is written.
 *
 * One line by default, exactly as `TodoComposer` — but with one difference that
 * is the whole module: the moment is **required**. A reminder with no time is a
 * todo, and there is a screen for those.
 *
 * What the field hands back is a local time with no offset, and turning that
 * into the instant the server stores is `fromLocalInput` in `lib/dates.ts` —
 * which is where the browser's timezone is read, here and in the row that can
 * move a reminder, so that the two cannot come to disagree about it.
 */
export function ReminderComposer({
  onCreate,
  pending,
}: {
  onCreate: (body: CreateReminderBody) => void;
  pending: boolean;
}): ReactElement {
  const t = useT();
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [remindAt, setRemindAt] = useState('');

  const ready = title.trim().length > 0 && remindAt !== '';

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (!ready) return;

    // A `datetime-local` can hand back a value the browser accepted and `Date`
    // cannot read. The server refuses it too, but refusing here means the field
    // keeps what was typed instead of clearing on a rejection.
    const moment = fromLocalInput(remindAt);
    if (!moment) return;

    onCreate({ title: title.trim(), remindAt: moment, tags });

    setTitle('');
    setTags([]);
    setRemindAt('');
  };

  const started = title.trim().length > 0;

  return (
    <form onSubmit={submit} className="rounded-lg border border-line bg-surface p-2">
      <div className="flex items-center gap-2">
        <svg
          viewBox="0 0 24 24"
          className="ml-1.5 size-5 shrink-0 text-ink-400"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        <input
          type="text"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={t('reminders.add')}
          aria-label={t('reminders.add')}
          disabled={pending}
          className="min-h-11 w-full bg-transparent text-base outline-none placeholder:text-ink-400"
        />
        {/* The button appears with something to submit rather than sitting there
            disabled: a control that cannot be pressed still has to be read. */}
        {ready && (
          <button
            type="submit"
            disabled={pending}
            className="min-h-9 shrink-0 rounded-lg bg-accent px-3 text-sm font-semibold text-accent-ink transition-colors hover:bg-accent-dim disabled:opacity-60"
          >
            {t('reminders.addAction')}
          </button>
        )}
      </div>

      {/* The moment appears with the title rather than with the button, because
          it is not optional: asked for only once the title is written, but asked
          for before there is anything to submit. */}
      {started && (
        <div className="mt-2 flex flex-wrap items-start gap-2 border-t border-line pt-2 pl-1.5">
          <input
            type="datetime-local"
            value={remindAt}
            onChange={(event) => setRemindAt(event.target.value)}
            aria-label={t('reminders.at')}
            required
            className="min-h-9 rounded-lg border border-line bg-paper px-2.5 text-sm text-ink-700 outline-none focus:border-accent"
          />
          <TagInput tags={tags} onChange={setTags} disabled={pending} />
        </div>
      )}
    </form>
  );
}
